/**
 * `SubscribeGate` — what a non-subscriber sees instead of the app (`spec.md` §9.8).
 *
 * The server refuses the launch with `403 NOT_SUBSCRIBED` and there is no session, so
 * there is no catalogue, no queue and no player to show. This screen is therefore a
 * **replacement** for the shell's contents, not an overlay over them, and it carries
 * exactly two controls:
 *
 *  1. `Открыть канал` — `telegram.openTelegramLink(joinUrl)`, the only thing that can
 *     change the situation. Rendered only when the server sent a link
 *     (`error.details.joinUrl`, from `TG_CHANNEL_JOIN_URL`): a button that opens nothing
 *     is worse than no button, so a deployment with no configured link gets the sentence
 *     alone.
 *  2. `Я подписался` — re-runs `POST /api/auth/telegram`. The server never caches a
 *     refusal (§9.8), so this answers truthfully the moment the user has joined, which is
 *     the whole reason the negative verdict is not cached.
 *
 * ## Not an `ErrorState`
 *
 * `NEEDLE SKIP` is the plate for "something broke, try again". Nothing is broken here —
 * the app is working exactly as configured — and `failure.retryable` is false for this
 * code, so `ErrorState` would render the sentence with no way out at all. The copy rule
 * is the same one every plate follows (§8.9): say what happened and what to do, never
 * apologise, never blame.
 */

import type { JSX } from 'react';

import { CropPlate } from '../../components/primitives/CropPlate';
import { cx } from '../../components/primitives/Rule';
import { telegram } from '../../lib/telegram';

import { StateFrame } from './Loading';
import styles from './states.module.css';

/** English label print, decorative, `aria-hidden` — the UI language is Russian (§11). */
export const GATE_MARK = 'MEMBERS ONLY';

/** Sentence case: tracked Cyrillic caps read as shouting (§4.3, §12.16). */
export const GATE_SENTENCE = 'Плеер открыт только для подписчиков канала.';
export const GATE_DETAIL = 'Подпишитесь на канал и вернитесь — доступ откроется сразу.';
export const GATE_JOIN_LABEL = 'Открыть канал';
export const GATE_RETRY_LABEL = 'Я подписался';

export interface SubscribeGateProps {
  /**
   * `error.details.joinUrl`. Absent ⇒ the join button is not rendered at all (see the
   * module comment), which is a configuration state, not an error to report.
   */
  readonly joinUrl?: string | undefined;
  /** Re-runs the launch exchange. */
  readonly onRetry: () => void;
  /** `true` while that exchange is in flight — both controls go inert. */
  readonly busy?: boolean | undefined;
}

/**
 * Pull `joinUrl` out of an `ApiFailure`'s `details` without trusting its shape.
 *
 * `details` is `unknown` by contract and crosses the network, so this narrows rather
 * than casts, and it accepts only `https:`/`tg:` links — an `http:` or `javascript:`
 * value is dropped, not opened.
 */
export function joinUrlFromDetails(details: unknown): string | undefined {
  if (typeof details !== 'object' || details === null) return undefined;
  const raw = (details as { joinUrl?: unknown }).joinUrl;
  if (typeof raw !== 'string' || raw.length === 0) return undefined;
  return /^(https:|tg:)/i.test(raw) ? raw : undefined;
}

export function SubscribeGate({ joinUrl, onRetry, busy = false }: SubscribeGateProps): JSX.Element {
  const openChannel = (): void => {
    if (joinUrl === undefined) return;
    telegram.openTelegramLink(joinUrl);
  };

  return (
    <StateFrame grid="plain" state="empty" role="status" ariaLabel={GATE_SENTENCE}>
      <span className={cx('t-mono', 't-11')} aria-hidden="true">
        {GATE_MARK}
      </span>

      <CropPlate>
        <div className={styles.plateBody}>
          <p className={cx('t-ui', 't-17', styles.sentence)} data-state-string="gate">
            {GATE_SENTENCE}
          </p>
          <p className={cx('t-ui', 't-15', styles.sentence)}>{GATE_DETAIL}</p>
        </div>
      </CropPlate>

      <div className={styles.gateActions}>
        {joinUrl === undefined ? null : (
          <button
            type="button"
            className={cx('t-ui', 't-17', styles.action, styles.primary)}
            onClick={openChannel}
            disabled={busy}
            data-action="join"
          >
            {GATE_JOIN_LABEL}
          </button>
        )}
        <button
          type="button"
          className={cx('t-ui', 't-17', styles.action)}
          onClick={onRetry}
          disabled={busy}
          data-action="recheck"
        >
          {GATE_RETRY_LABEL}
        </button>
      </div>
    </StateFrame>
  );
}
